/**
 * src/hooks/useTimeSlots.js — Server-synced time slot generation.
 *
 * Uses the server-provided `now()` function to generate slot timestamps.
 * All clients produce identical slot IDs and times regardless of
 * local timezone or OS clock.
 *
 * Times are displayed in Eastern (via easternNow) but slot IDs
 * use UTC timestamps for server consistency.
 */

import { useEffect, useRef } from 'react';
import { COINS } from '../constants';

const useTimeSlots = (prices, setTimeSlots, onMinuteChange, isPaused, serverNow) => {
    const pricesRef = useRef(prices);
    useEffect(() => { pricesRef.current = prices; }, [prices]);

    useEffect(() => {
        if (isPaused) return;
        let lastCapturedMin = -1;

        const generateSlots = (isRollover = false) => {
            // Use server-synced time, fallback to client clock
            const date = serverNow ? serverNow() : new Date();
            const now = new Date(
                date.getFullYear(), date.getMonth(), date.getDate(),
                date.getHours(), date.getMinutes(), 0, 0
            );
            const currentMin = now.getMinutes();

            const slots = {};
            COINS.forEach(coin => {
                slots[coin.id] = [];
                for (let i = 0; i < 11; i++) {
                    const targetTime = new Date(now.getTime() + i * 60000);
                    const hour = targetTime.getHours();
                    const minute = targetTime.getMinutes();
                    const displayTime = `${hour % 12 || 12}:${String(minute).padStart(2, '0')}`;
                    slots[coin.id].push({
                        id: `slot_${targetTime.getTime()}`,
                        time: displayTime,
                        isCapturing: i === 0,
                        isBirth: i === 10
                    });
                }
            });

            const hour = now.getHours();
            const min = now.getMinutes();
            const timeStr = `${hour % 12 || 12}:${String(min).padStart(2, '0')}`;

            setTimeSlots(slots);

            // Only trigger onMinuteChange if we are at the top of a NEW minute
            if (isRollover && currentMin !== lastCapturedMin) {
                lastCapturedMin = currentMin;
                if (onMinuteChange) onMinuteChange(pricesRef.current, timeStr);
            }
        };

        generateSlots(false); // Initial load without settlement

        const checkTime = () => {
            // Use server-synced time for second detection
            const date = serverNow ? serverNow() : new Date();
            if (date.getSeconds() === 0) {
                generateSlots(true); // Rollover with settlement
            }
        };

        const interval = setInterval(checkTime, 1000);
        return () => clearInterval(interval);
    }, [setTimeSlots, onMinuteChange, isPaused, serverNow]);
};

export default useTimeSlots;
